// Web Push (VAPID): уведомления о новых сообщениях, когда вкладка HomeChat закрыта.
// Пока вкладка открыта, уведомления показывает notifications.ts по событиям WS; сервер шлёт
// push только пользователям без живого WS-подключения (см. backend PushService), а sw.js
// дополнительно глушит push, если окно сайта в фокусе.
import { ApiError } from './api/client';
import { getChat, history, listRooms, markRead } from './api/chats';
import { deletePushSubscription, getVapidPublicKey, savePushSubscription } from './api/push';

export type PushState =
  | 'unsupported' // браузер не умеет (нет SW/PushManager, или не secure context)
  | 'unavailable' // на сервере не заданы VAPID-ключи
  | 'denied' // пользователь запретил уведомления в браузере
  | 'off'
  | 'on';

const SW_URL = '/sw.js';
// Пользователь сам выключил push в настройках — автопереподписка его выбор не перебивает.
const OPT_OUT_KEY = 'hc_push_opt_out';

function setOptOut(value: boolean): void {
  try {
    if (value) localStorage.setItem(OPT_OUT_KEY, '1');
    else localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    /* localStorage недоступен — не критично */
  }
}

function isOptedOut(): boolean {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
  );
}

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

/** Регистрирует sw.js один раз за жизнь страницы; null — если браузер не умеет. */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return Promise.resolve(null);
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker.register(SW_URL).catch(() => null);
  }
  return registrationPromise;
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await registerServiceWorker();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function getPushState(): Promise<PushState> {
  if (!isPushSupported()) return 'unsupported';
  try {
    await getVapidPublicKey();
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return 'unavailable';
    throw e;
  }
  if (Notification.permission === 'denied') return 'denied';
  return (await currentSubscription()) ? 'on' : 'off';
}

/** Включение: разрешение → подписка в браузере → сохранение на сервере. Возвращает итоговое состояние. */
export async function enablePush(): Promise<PushState> {
  if (!isPushSupported()) return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';
  const reg = await registerServiceWorker();
  if (!reg) return 'unsupported';
  let key: string;
  try {
    key = (await getVapidPublicKey()).publicKey;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return 'unavailable';
    throw e;
  }
  let sub = await reg.pushManager.getSubscription();
  // Подписка с другим ключом сервера (ключи сменили) push-сервис отвергнет — пересоздаём.
  if (sub && !sameKey(sub.options.applicationServerKey, key)) {
    await sub.unsubscribe();
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(key) });
  }
  await savePushSubscription(sub.toJSON());
  setOptOut(false);
  return 'on';
}

/** Выключение: убрать подписку с сервера и из браузера. Ошибки сервера не мешают отписке в браузере. */
export async function disablePush(markOptOut = true): Promise<void> {
  if (markOptOut) setOptOut(true);
  const sub = await currentSubscription();
  if (!sub) return;
  try {
    await deletePushSubscription(sub.endpoint);
  } finally {
    await sub.unsubscribe().catch(() => false);
  }
}

/** При входе: если браузер уже подписан — перепривязать подписку к текущему аккаунту/сессии
 *  (сервер не шлёт push по подпискам отозванных сессий). Без подписки ничего не делает. */
let syncRunning: Promise<void> | null = null;
let syncAgain = false;

export async function syncPushSubscription(): Promise<void> {
  if (!isPushSupported() || Notification.permission !== 'granted') return;
  // Запросы не пересекаются: сервер берёт язык из Accept-Language, и поздний ответ старого запроса
  // не должен вернуть прежний язык. Вызов во время работы помечает повтор с актуальным языком.
  if (syncRunning) {
    syncAgain = true;
    return syncRunning;
  }
  syncRunning = (async () => {
    try {
      do {
        syncAgain = false;
        try {
          const sub = await currentSubscription();
          if (sub) await savePushSubscription(sub.toJSON());
          else if (!isOptedOut()) await enablePush(); // разрешение есть, подписки нет (потеряна) — переподписка без запроса
        } catch {
          /* push выключен на сервере или сеть — не критично */
        }
      } while (syncAgain);
    } finally {
      syncRunning = null;
    }
  })();
  return syncRunning;
}

/** Перед выходом из аккаунта: чужой следующий вход в этом браузере не должен получать наши push. */
export async function disablePushOnLogout(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    await disablePush(false); // выход из аккаунта — не отказ от push: опт-аут не ставим
  } catch {
    /* сеть/сервер — не повод блокировать выход; сессия всё равно отзывается, сервер такие подписки не использует */
  }
}

/** Открыть чат по клику на push-уведомление: при уже открытой вкладке — сообщение от sw.js,
 *  при новой вкладке — параметр ?chat=<id> (sw.js открывает /?chat=...). */
export function listenPushNavigation(openChat: (chatId: string) => void, readChat?: (chatId: string) => void): () => void {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('chat');
  if (fromUrl) {
    const action = params.get('notificationAction');
    params.delete('notificationAction');
    params.delete('chat');
    const rest = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (rest ? '?' + rest : '') + window.location.hash);
    if (action === 'read' && readChat) readChat(fromUrl);
    else openChat(fromUrl);
  }
  if (!('serviceWorker' in navigator)) return () => {};
  const onMessage = (e: MessageEvent) => {
    if (e.data?.type === 'open-chat' && typeof e.data.chatId === 'string') {
      if (e.data.action === 'read' && readChat) readChat(e.data.chatId);
      else openChat(e.data.chatId);
    }
  };
  navigator.serviceWorker.addEventListener('message', onMessage);
  return () => navigator.serviceWorker.removeEventListener('message', onMessage);
}

function base64UrlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function sameKey(current: ArrayBuffer | null, key: string): boolean {
  if (!current) return false;
  const a = new Uint8Array(current);
  const b = base64UrlToBytes(key);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** Явное действие системного уведомления: читаем актуальные границы каждой комнаты. */
export async function markNotificationChatRead(chatId: string): Promise<void> {
  const chat = await getChat(chatId);
  const rooms = chat.hasRooms ? [null, ...(await listRooms(chatId)).map(room => room.id)] : [null];
  await Promise.all(rooms.map(async roomId => {
    const page = await history(chatId, undefined, 1, roomId);
    const last = page.items[0];
    if (last) await markRead(chatId, last.id, roomId);
  }));
}
